import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-meter.css?inline';

/**
 * `<pk-meter>` — a thin horizontal bar + text label showing a percentage.
 *
 * Attributes:
 *   - `value`  — number 0–100 (clamped); the fill level
 *   - `label`  — optional text label shown beside the bar
 *   - `tone`   — optional explicit override: `success` | `info` | `warn`.
 *               Auto-derives from value when omitted (<40 warn, <75 info, >=75 success).
 *
 * Properties:
 *   - `segments` — optional array of `{ label: string, value: number, tone?: string }`
 *                  for a stacked / segmented bar; when set, overrides single-bar rendering.
 *
 * Accessibility: host has role="meter", aria-valuenow, aria-valuemin="0", aria-valuemax="100".
 * The numeric value is visible as text (e.g. "88%").
 */
export interface PkMeterSegment {
    label: string;
    value: number;
    tone?: string;
}

export class PkMeter extends HTMLElement {
    static readonly tagName = 'pk-meter';

    static get observedAttributes(): string[] {
        return ['value', 'label', 'tone'];
    }

    private readonly _root: ShadowRoot;
    private _segments: PkMeterSegment[] | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._syncAriaAttrs();
        this._render();
    }

    attributeChangedCallback(): void {
        this._syncAriaAttrs();
        this._render();
    }

    /** JS-settable segments for stacked mode. Re-renders on assignment. */
    get segments(): PkMeterSegment[] | null {
        return this._segments;
    }

    set segments(val: PkMeterSegment[] | null) {
        this._segments = val;
        this._render();
    }

    private get _value(): number {
        const raw = parseFloat(this.getAttribute('value') ?? '0');
        return Math.max(0, Math.min(100, Number.isFinite(raw) ? raw : 0));
    }

    private get _tone(): string {
        const explicit = this.getAttribute('tone');
        if (explicit) return explicit;
        const v = this._value;
        if (v < 40)  return 'warn';
        if (v < 75)  return 'info';
        return 'success';
    }

    private _toneColor(tone: string): string {
        if (tone === 'success') return 'var(--pk-color-success, #2A7E4F)';
        if (tone === 'warn')    return 'var(--pk-color-warn, #C77E07)';
        // info — no dedicated token, fall back to primary
        return 'var(--pk-color-primary, #0E7C7B)';
    }

    private _syncAriaAttrs(): void {
        this.setAttribute('role', 'meter');
        this.setAttribute('aria-valuemin', '0');
        this.setAttribute('aria-valuemax', '100');
        this.setAttribute('aria-valuenow', String(this._value));
        const label = this.getAttribute('label');
        if (label) {
            this.setAttribute('aria-label', label);
        }
    }

    private _template(): TemplateResult {
        const value = this._value;
        const label = this.getAttribute('label') ?? '';

        if (this._segments && this._segments.length > 0) {
            // Stacked segmented bar
            const total = this._segments.reduce((s, seg) => s + seg.value, 0);
            const segments = this._segments;
            return html`
                <style>${styles}</style>
                <div class="meter">
                    ${label ? html`<span class="meter__label" id="meter-label">${label}</span>` : ''}
                    <div class="meter__track meter__track--segmented" aria-hidden="true">
                        ${segments.map(seg => {
                            const pct = total > 0 ? Math.max(0, Math.min(100, (seg.value / total) * 100)) : 0;
                            const tone = seg.tone ?? 'info';
                            const color = this._toneColor(tone);
                            return html`<div
                                class="meter__fill meter__fill--segment"
                                style="width:${pct.toFixed(2)}%;background:${color}"
                                title="${seg.label}: ${seg.value.toFixed(0)}%"
                            ></div>`;
                        })}
                    </div>
                    <div class="meter__values" aria-hidden="true">
                        ${segments.map(seg => html`
                            <span class="meter__chip meter__chip--${seg.tone ?? 'info'}"
                                  style="color:${this._toneColor(seg.tone ?? 'info')}">
                                ${seg.label}: ${seg.value.toFixed(0)}%
                            </span>
                        `)}
                    </div>
                </div>
            `;
        }

        // Single-bar mode
        const tone = this._tone;
        const color = this._toneColor(tone);
        const labelId = 'meter-label';
        return html`
            <style>${styles}</style>
            <div class="meter">
                ${label ? html`<span class="meter__label" id="${labelId}">${label}</span>` : ''}
                <div class="meter__track" aria-hidden="true">
                    <div
                        class="meter__fill meter__fill--${tone}"
                        style="width:${value.toFixed(2)}%;background:${color}"
                    ></div>
                </div>
                <span class="meter__value" aria-hidden="true">${value.toFixed(0)}%</span>
            </div>
        `;
    }

    private _render(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkMeter.tagName)) {
    customElements.define(PkMeter.tagName, PkMeter);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-meter': PkMeter;
    }
}
